import {
  useFeatureRequests,
  useResolvedTheme,
  type FeatureRequest,
} from "@wherdkit/core";
import { useState } from "react";
import { Pressable, ScrollView, Text, TextInput, useColorScheme, View } from "react-native";

function useToks() {
  const theme = useResolvedTheme(useColorScheme() ?? "light");
  return {
    surface: theme.colors.background,
    text: theme.colors.foreground,
    sub: theme.colors.mutedForeground,
    border: theme.colors.border,
    field: theme.colors.background,
    fieldBorder: theme.colors.border,
    accent: theme.colors.primary,
    accentFg: theme.colors.primaryForeground,
  };
}

const STATUS: Partial<Record<FeatureRequest["status"], { label: string; color: string; bg: string }>> = {
  planned: { label: "Planned", color: "#b45309", bg: "#fef3c7" },
  in_progress: { label: "In progress", color: "#2563eb", bg: "#dbeafe" },
  done: { label: "Shipped", color: "#15803d", bg: "#dcfce7" },
};

export function FeatureBoard() {
  const t = useToks();
  const { requests, loading, vote, create } = useFeatureRequests();
  const [title, setTitle] = useState("");
  const [creating, setCreating] = useState(false);

  const submit = async () => {
    if (!title.trim()) return;
    setCreating(true);
    await create({ title: title.trim() });
    setTitle("");
    setCreating(false);
  };

  return (
    <View style={{ flex: 1 }}>
      <View style={{ flexDirection: "row", gap: 8, marginBottom: 14 }}>
        <TextInput
          value={title} onChangeText={setTitle} placeholder="Suggest a feature…" placeholderTextColor={t.sub}
          style={{ flex: 1, paddingHorizontal: 13, paddingVertical: 11, borderRadius: 10, borderWidth: 1.5, borderColor: t.fieldBorder, backgroundColor: t.field, color: t.text, fontSize: 14 }}
        />
        <Pressable onPress={() => void submit()} disabled={creating || !title.trim()} style={{ justifyContent: "center", paddingHorizontal: 18, borderRadius: 10, backgroundColor: t.accent, opacity: creating || !title.trim() ? 0.6 : 1 }}>
          <Text style={{ color: t.accentFg, fontWeight: "700" }}>Add</Text>
        </Pressable>
      </View>

      {loading ? (
        <Text style={{ color: t.sub, fontSize: 14 }}>Loading…</Text>
      ) : requests.length === 0 ? (
        <Text style={{ color: t.sub, fontSize: 14 }}>No requests yet — be the first to suggest something.</Text>
      ) : (
        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 6 }}>
          <View style={{ gap: 8 }}>
            {requests.map((r) => {
              const m = STATUS[r.status];
              return (
                <View key={r.id} style={{ flexDirection: "row", alignItems: "center", gap: 12, borderWidth: 1, borderColor: t.border, borderRadius: 12, padding: 10, backgroundColor: t.surface }}>
                  <Pressable onPress={() => void vote(r.id, !r.hasVoted)} style={{ alignItems: "center", justifyContent: "center", minWidth: 44, paddingVertical: 6, borderRadius: 10, borderWidth: 1.5, borderColor: r.hasVoted ? t.accent : t.fieldBorder, backgroundColor: r.hasVoted ? t.accent : "transparent" }}>
                    <Text style={{ fontSize: 11, color: r.hasVoted ? t.accentFg : t.sub }}>▲</Text>
                    <Text style={{ fontSize: 12, fontWeight: "800", color: r.hasVoted ? t.accentFg : t.sub }}>{r.voteCount}</Text>
                  </Pressable>
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 14.5, fontWeight: "700", color: t.text }}>{r.title}</Text>
                    {m ? (
                      <View style={{ alignSelf: "flex-start", marginTop: 5, paddingHorizontal: 9, paddingVertical: 2, borderRadius: 999, backgroundColor: m.bg }}>
                        <Text style={{ fontSize: 11, fontWeight: "700", color: m.color }}>{m.label}</Text>
                      </View>
                    ) : null}
                  </View>
                </View>
              );
            })}
          </View>
        </ScrollView>
      )}
    </View>
  );
}
